import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { TrendingUp } from 'lucide-react';
import type { ScoreEvolutionPoint } from '@/types/session.ts';
import { formatDateShort } from '@/utils/formatDate.ts';
import { toScore99 } from '@/utils/scoreFormat.ts';

interface ScoreEvolutionChartProps {
  data: ScoreEvolutionPoint[];
}

export default function ScoreEvolutionChart({ data }: ScoreEvolutionChartProps) {
  const { t } = useTranslation();

  if (data.length < 2) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted py-16">
        <TrendingUp className="h-10 w-10 text-muted-foreground/40" />
        <p className="mt-4 text-sm font-medium text-muted-foreground">
          {t('progress.chartMinSessions')}
        </p>
        <p className="mt-1 text-xs text-muted-foreground/60">
          {t('progress.chartMinSessionsSub')}
        </p>
      </div>
    );
  }

  const chartData = data.map((point) => ({
    date: formatDateShort(point.date),
    score: toScore99(point.score),
  }));

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
          />
          <YAxis
            domain={[0, 99]}
            tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
            width={32}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelStyle={{ color: 'var(--color-text-secondary)' }}
            formatter={(value: number | undefined) => [value ?? '—', 'Score']}
          />
          <Line
            type="monotone"
            dataKey="score"
            stroke="var(--primary)"
            strokeWidth={2.5}
            dot={{ r: 4, fill: 'var(--primary)', strokeWidth: 0 }}
            activeDot={{ r: 6, fill: 'var(--primary)', strokeWidth: 2, stroke: 'var(--surface)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
